/**
 * Browser CPU telemetry reported through the standard logging pipeline.
 *
 * Browsers expose no direct CPU-percentage API, so this module logs two proxy
 * signals plus static device context:
 *
 * - `web.performance.pressure` — Compute Pressure API state transitions
 *   (nominal/fair/serious/critical). Chromium 125+; a system-level "is the
 *   device overloaded" signal. Event-driven, so volume is naturally low.
 * - `web.performance.longtasks` — main-thread blocking, aggregated into chunks
 *   bounded by whatever the app calls a meaningful interaction, plus tab-hide
 *   and upward CPU-pressure transitions. One summary event per non-clean
 *   chunk; clean chunks (zero long tasks) are never logged, which is what
 *   holds this at or below interaction-event volume rather than at long-task
 *   volume.
 * - `getDeviceContext()` — hardwareConcurrency / deviceMemory, for spreading
 *   into session-start-shaped payloads as a device-class dimension.
 *
 * Mount `<PerformanceTelemetry />` once, beside `<WebVitals />` — in this
 * template that is apps/retells/src/logging/LoggerProvider.tsx. Chunk boundaries
 * are reported by calling `flushPerformanceChunk()` from interaction sites.
 *
 * Both features are progressive: on a browser without the Compute Pressure
 * API, or without `PerformanceObserver`, the corresponding effect returns
 * without observing and nothing is ever emitted.
 */

import { useEffect, useRef } from "react";
import type { Logger } from "./logger";

// --- Compute Pressure API (Chromium 125+; not yet in lib.dom) ---

type PressureState = "nominal" | "fair" | "serious" | "critical";

interface PressureRecord {
  source: string;
  state: PressureState;
  time: number;
}

interface PressureObserverInstance {
  observe(source: "cpu", options?: { sampleInterval?: number }): Promise<void>;
  disconnect(): void;
}

type PressureObserverConstructor = new (
  callback: (records: PressureRecord[]) => void,
) => PressureObserverInstance;

/** Upper bound on pressure callback frequency (one state change per 2s). */
const PRESSURE_SAMPLE_INTERVAL_MS = 2000;

/** Severity order, for detecting *upward* pressure transitions. */
const PRESSURE_RANK: Record<PressureState, number> = {
  nominal: 0,
  fair: 1,
  serious: 2,
  critical: 3,
};

// --- Long Animation Frames (Chromium 123+; not yet in lib.dom) ---

interface LongAnimationFrameEntry extends PerformanceEntry {
  blockingDuration?: number;
}

/**
 * Why a long-task chunk was closed.
 *
 * Two of these — `visibility_hidden` and `pressure_increase` — are emitted by
 * this module itself; the other two are names for app-side call sites to use.
 * The union stays open —
 * `(string & {})` gives autocomplete without closing the set, the same pattern
 * `EventName` uses — because the interaction that bounds a chunk is
 * app-specific: a reading app flushes on `"word_engagement"`, a checkout flow
 * on `"step_advance"`. A project naming its own reason does not edit this
 * package, and `chunk_reason` is a `data` key rather than an event name, so no
 * vocabulary test governs it.
 */
export type PerformanceChunkReason =
  | "interaction"
  | "page_navigation"
  | "visibility_hidden"
  | "pressure_increase"
  | (string & {});

interface LongTaskChunk {
  /** performance.now() when this chunk started accumulating */
  startedAt: number;
  count: number;
  blockingMs: number;
  maxTaskMs: number;
}

// Module-level so flushPerformanceChunk() can be called from any interaction
// site — including non-React ones — without threading the accumulator through
// React context. The trade is that exactly one <PerformanceTelemetry /> may be
// mounted — which is the natural shape (one main thread, one set of long
// tasks), but is not enforced, so state the failure: two instances leave two
// live PerformanceObservers feeding this one accumulator, double-counting
// every task, and then the *first* unmount nulls the shared state out from
// under the survivor, silencing it for the rest of the page.
//
// A Strict Mode replay guard, of the kind WebVitals needs, would not help with
// that and is not needed for its own case: the dev-only mount → cleanup →
// mount is sequential, and every observer here is disconnected by its effect's
// cleanup (unlike web-vitals' reporters, which have no unsubscribe at all).
let activeChunk: LongTaskChunk | null = null;
let observerEntryType: "long-animation-frame" | "longtask" | null = null;
let telemetryLogger: Logger | null = null;

/**
 * Close the current long-task chunk and start a new one.
 *
 * Emits a `web.performance.longtasks` summary only when the chunk saw at least
 * one long task — clean chunks stay silent (see the module header).
 *
 * A no-op when the observer isn't running: unsupported browser, component not
 * mounted, or called after unmount. There is deliberately no fallback logger
 * here — an event with no mounted reporter behind it would carry none of the
 * correlation context the provider supplies, and the chunk it summarized was
 * never accumulated in the first place.
 *
 * @param reason - The boundary that closed this chunk
 * @param context - Extra correlation keys for the `data` payload (route,
 *   feature id, …). Subject to the same PII rules as any other `data` key:
 *   opaque ids and bounded values only, never raw user content.
 */
export function flushPerformanceChunk(
  reason: PerformanceChunkReason,
  context?: Record<string, unknown>,
): void {
  if (!activeChunk || !observerEntryType) return;

  const now = performance.now();
  const { startedAt, count, blockingMs, maxTaskMs } = activeChunk;
  activeChunk = { startedAt: now, count: 0, blockingMs: 0, maxTaskMs: 0 };

  if (count === 0) return;

  telemetryLogger?.event("web.performance.longtasks", {
    chunk_reason: reason,
    chunk_duration_ms: Math.round(now - startedAt),
    long_task_count: count,
    blocking_ms: Math.round(blockingMs),
    max_task_ms: Math.round(maxTaskMs),
    observer_type: observerEntryType,
    ...context,
  });
}

/**
 * Static device-class context, for spreading into session-scoped payloads so
 * pressure and long-task signals can be sliced by device capability.
 *
 * `deviceMemory` is Chromium-only and capped at 8 by spec, so `device_memory_gb`
 * is routinely present-but-`undefined` (it disappears at JSON serialization
 * rather than being omitted from the object).
 *
 * The browser test is `document`, not `navigator`: Node 21+ and Bun both define
 * a global `navigator` carrying the *host's* `hardwareConcurrency` (verified —
 * `bun -e` and `node -e` on v22 each report 8 on this machine), so a
 * navigator-based guard would return the build machine's core count from the
 * prerender pass and quietly poison the device-class dimension this exists to
 * provide. With the `document` test it returns `{}` anywhere there is no DOM,
 * which is what makes it safe to spread unconditionally.
 */
export function getDeviceContext(): {
  hardware_concurrency?: number;
  device_memory_gb?: number;
} {
  if (typeof document === "undefined" || typeof navigator === "undefined") {
    return {};
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    hardware_concurrency: nav.hardwareConcurrency,
    device_memory_gb: nav.deviceMemory,
  };
}

export interface PerformanceTelemetryProps {
  logger: Logger;
}

/**
 * CPU telemetry reporter component.
 *
 * Takes a `Logger` as a prop for the same reason `WebVitals` does: the
 * composition root stays the one place that builds a `Logger`.
 *
 * @example
 * ```tsx
 * <WebVitals logger={log} />
 * <PerformanceTelemetry logger={log} />
 * ```
 */
export function PerformanceTelemetry({ logger }: PerformanceTelemetryProps) {
  const logRef = useRef<Logger>(logger);

  // Keep the ref current without re-registering observers; the module-level
  // handle is what lets flushPerformanceChunk() reach the same instance from
  // a call site that has no props.
  useEffect(() => {
    logRef.current = logger;
    telemetryLogger = logger;
    return () => {
      if (telemetryLogger === logger) telemetryLogger = null;
    };
  }, [logger]);

  // Long-task accumulation (chunks closed by interaction sites)
  useEffect(() => {
    // Old WebViews lack PerformanceObserver entirely; Safari 11–12 has the
    // constructor but not the static supportedEntryTypes.
    const supported =
      typeof PerformanceObserver === "undefined"
        ? undefined
        : PerformanceObserver.supportedEntryTypes;
    if (!supported) return;
    const entryType = supported.includes("long-animation-frame")
      ? ("long-animation-frame" as const)
      : supported.includes("longtask")
        ? ("longtask" as const)
        : null;
    if (!entryType) return;

    observerEntryType = entryType;
    activeChunk = {
      startedAt: performance.now(),
      count: 0,
      blockingMs: 0,
      maxTaskMs: 0,
    };

    const observer = new PerformanceObserver((list) => {
      if (!activeChunk) return;
      for (const entry of list.getEntries()) {
        // LoAF's blockingDuration already subtracts the 50ms frame budget;
        // the longtask fallback approximates the same convention.
        const blocking =
          (entry as LongAnimationFrameEntry).blockingDuration ??
          Math.max(0, entry.duration - 50);
        activeChunk.count += 1;
        activeChunk.blockingMs += blocking;
        activeChunk.maxTaskMs = Math.max(activeChunk.maxTaskMs, entry.duration);
      }
    });
    // buffered: true folds pre-mount tasks (hydration) into the first chunk
    observer.observe({ type: entryType, buffered: true });

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushPerformanceChunk("visibility_hidden");
        // A no-op against the backends this template ships — WorkerLogBackend
        // POSTs with `keepalive` on every write and ConsoleLogBackend has
        // nothing to drain — but the tab may never come back, so the call
        // belongs here for whatever batching backend a project adds later.
        void logRef.current.flush();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      activeChunk = null;
      observerEntryType = null;
    };
  }, []);

  // Compute Pressure state transitions
  useEffect(() => {
    // `window`, not `globalThis`: `PressureObserver` is missing from lib.dom
    // so it has to be read off a cast global, and the browser-compat lint
    // (`es-x/no-global-this`, keyed to BROWSER_FLOOR) warns on `globalThis` in
    // bundled code. This effect only ever runs in a browser, where `window` is
    // the same object and needs no floor exception.
    const PressureObserverCtor = (
      window as { PressureObserver?: PressureObserverConstructor }
    ).PressureObserver;
    if (!PressureObserverCtor) return;

    let prevState: PressureState | null = null;
    let prevStateSince: number | null = null;

    let observer: PressureObserverInstance;
    try {
      observer = new PressureObserverCtor((records) => {
        for (const record of records) {
          if (record.state === prevState) continue;
          // A rise in CPU pressure closes the long-task chunk, so the blocking
          // summary for the window leading up to the rise lands adjacent to
          // the pressure event rather than being folded into the next chunk.
          if (
            prevState !== null &&
            PRESSURE_RANK[record.state] > PRESSURE_RANK[prevState]
          ) {
            flushPerformanceChunk("pressure_increase", {
              pressure_state: record.state,
              prev_pressure_state: prevState,
            });
          }
          logRef.current.event("web.performance.pressure", {
            source: "cpu",
            state: record.state,
            prev_state: prevState ?? undefined,
            time_in_prev_state_ms:
              prevStateSince === null
                ? undefined
                : Math.round(record.time - prevStateSince),
          });
          prevState = record.state;
          prevStateSince = record.time;
        }
      });
    } catch {
      return;
    }

    // Rejects with NotSupportedError on platforms without a "cpu" source —
    // the signal is best-effort, so swallow it.
    observer
      .observe("cpu", { sampleInterval: PRESSURE_SAMPLE_INTERVAL_MS })
      .catch(() => {
        // unsupported source; nothing to clean up
      });

    return () => observer.disconnect();
  }, []);

  return null;
}
